import { useEffect, useMemo, useState } from 'react';
import { SkeletonRow } from '@/components/Skeleton';
import { ErrorState } from '@/components/ErrorState';
import { PasswordInput } from '@/components/PasswordInput';
import { PencilIcon } from '@/components/icons/PencilIcon';
import {
  useCreateUserMutation,
  useGetCurrentUserQuery,
  useListUsersQuery,
  useSetUserRoleMutation
} from '@/services/api';
import { useToast } from '@/components/toast/ToastProvider';
import { parseApiError } from '@/utils/apiError';
import { checkPasswordPolicy, isValidEmail } from '@/utils/auth';
import { formatTimestamp } from '@/utils/format';
import type { AdminUserDto, UserRole } from '@/types/api';

const PAGE_SIZES = [10, 25, 50, 100] as const;

const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  admin: 'Full control — user management, projects, members, and approve/deny at every pause.',
  approver: 'Run pipelines plus approve or deny at every pipeline pause. No user or project administration.',
  task_submitter: 'Run pipelines and view results. Cannot approve, or manage users, projects, or members.'
};

export function UsersPage(): JSX.Element {
  const userQ = useGetCurrentUserQuery();
  const usersQ = useListUsersQuery(undefined, { skip: userQ.data?.role !== 'admin' });

  if (userQ.isLoading) {
    return (
      <div className="content" style={{ paddingTop: 48 }}>
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }
  if (userQ.isError || !userQ.data) {
    return (
      <div className="content" style={{ paddingTop: 48 }}>
        <ErrorState
          title="Couldn't load your account"
          body="The account service is unreachable."
          onRetry={() => void userQ.refetch()}
        />
      </div>
    );
  }

  const user = userQ.data;

  if (user.role !== 'admin') {
    return (
      <>
        <h1 className="page-title">Users</h1>
        <div className="card">
          <div className="empty" style={{ padding: '80px 24px' }}>
            <p className="empty-title">You don&apos;t have access to user management</p>
            <p className="empty-sub">
              User management is restricted to administrators.
            </p>
            <div className="mono-cap" style={{ marginBottom: 16 }}>
              capability required · user.manage
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <UsersAdmin currentUserId={user.user_id} usersQ={usersQ} />
    </>
  );
}

function UsersAdmin({
  currentUserId,
  usersQ
}: {
  currentUserId: string;
  usersQ: ReturnType<typeof useListUsersQuery>;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>('all');
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);
  const [dialog, setDialog] = useState<
    { kind: 'create' } | { kind: 'change'; user: AdminUserDto } | null
  >(null);

  const users: ReadonlyArray<AdminUserDto> = usersQ.data?.users ?? [];
  const adminCount = users.filter((u) => u.system_role === 'admin' && u.status === 'active').length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== 'all' && u.system_role !== roleFilter) return false;
      if (!q) return true;
      return (
        u.email.toLowerCase().includes(q) || (u.display_name ?? '').toLowerCase().includes(q)
      );
    });
  }, [users, search, roleFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  const pageStart = page * pageSize;
  const visible = filtered.slice(pageStart, pageStart + pageSize);

  if (usersQ.isLoading) {
    return (
      <div className="card">
        <div className="card-head">
          <div className="card-title">Users</div>
        </div>
        <div className="members-toolbar" />
        <div className="overflow-x-auto">
          <table className="users-table" aria-busy="true" aria-label="Loading users">
            <thead>
              <tr>
                <th>User</th>
                <th className="user-email-col">Email</th>
                <th style={{ width: 150 }}>Role</th>
                <th style={{ width: 120 }}>Last login</th>
                <th style={{ width: 88, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: pageSize }, (_, i) => (
                <tr key={i}>
                  {Array.from({ length: 5 }, (_, j) => (
                    <td key={j}>
                      <div
                        className="animate-skel"
                        style={{
                          height: 12,
                          borderRadius: 3,
                          background: 'var(--surface-sunken)',
                          width: `${45 + ((i + j) % 4) * 12}%`
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  if (usersQ.isError) {
    return (
      <ErrorState
        title="Couldn't load users"
        body="The account service is unreachable."
        onRetry={() => void usersQ.refetch()}
      />
    );
  }

  return (
    <>
      <div className="card">
        <div className="card-head">
          <div className="card-title">Users · {users.length}</div>
          <button className="btn primary sm" onClick={() => setDialog({ kind: 'create' })}>
            + Create user
          </button>
        </div>
        <div className="members-toolbar">
          <input
            className="search-input"
            autoComplete="off-user-search"
            placeholder="Search by email or name…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            aria-label="Search users"
          />
          <select
            className="toolbar-select"
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as 'all' | UserRole);
              setPage(0);
            }}
            aria-label="Filter by role"
          >
            <option value="all">All roles</option>
            <option value="admin">admin</option>
            <option value="approver">approver</option>
            <option value="task_submitter">task_submitter</option>
          </select>
        </div>
        <div className="overflow-x-auto">
        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th className="user-email-col">Email</th>
              <th style={{ width: 150 }}>Role</th>
              <th style={{ width: 120 }}>Last login</th>
              <th style={{ width: 88, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((u) => {
              const isSelf = u.user_id === currentUserId;
              const isOnlyAdmin = u.system_role === 'admin' && adminCount <= 1;
              return (
                <tr key={u.user_id}>
                  <td className="member-col">
                    <div className="member-cell">
                      <span className={`member-avatar ${isSelf ? '' : 'alt'}`}>
                        {initials(u.display_name ?? u.email)}
                      </span>
                      <div>
                        <div className="member-name" title={u.display_name ?? u.email}>
                          {u.display_name ?? u.email}
                        </div>
                        {isSelf ? <div className="member-you">you</div> : null}
                      </div>
                    </div>
                  </td>
                  <td className="user-email-col">
                    <span className="member-email" title={u.email}>
                      {u.email}
                    </span>
                  </td>
                  <td>
                    <span className={`role-chip ${u.system_role === 'admin' ? 'admin' : ''}`}>
                      {u.system_role}
                    </span>
                  </td>
                  <td>
                    <span className="ts">
                      {u.last_login_at ? formatTimestamp(u.last_login_at).slice(0, 10) : '—'}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions" style={{ justifyContent: 'flex-end' }}>
                      <button
                        className="icon-btn"
                        disabled={isOnlyAdmin}
                        aria-label={`Edit role for ${u.display_name ?? u.email}`}
                        title={isOnlyAdmin ? 'Cannot change the only admin' : 'Edit role'}
                        onClick={() => setDialog({ kind: 'change', user: u })}
                      >
                        <PencilIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {filtered.length === 0 ? (
          <div className="empty" style={{ padding: '48px 24px' }}>
            <p className="empty-title">No users match this search</p>
            <p className="empty-sub">Clear the search box or choose a different role.</p>
          </div>
        ) : null}

        <div className="pagination">
          <span className="pager-info">
            {filtered.length === 0
              ? '0 users'
              : `${pageStart + 1}–${pageStart + visible.length} of ${filtered.length}`}
            {filtered.length !== users.length ? ` · filtered from ${users.length}` : ''}
          </span>
          <div className="pager-controls" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="mono-cap">Per page</span>
            <select
              className="select sm-sel"
              style={{ width: 80 }}
              aria-label="Users per page"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <button
              className="btn ghost sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Prev
            </button>
            <span className="pager-info">
              Page {page + 1} of {pageCount}
            </span>
            <button
              className="btn ghost sm"
              disabled={page >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {dialog?.kind === 'create' && <CreateUserDialog onClose={() => setDialog(null)} />}
      {dialog?.kind === 'change' && (
        <ChangeUserRoleDialog user={dialog.user} onClose={() => setDialog(null)} />
      )}
    </>
  );
}

function CreateUserDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const [createUser, { isLoading }] = useCreateUserMutation();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('task_submitter');
  const [error, setError] = useState<string | null>(null);

  const passwordCheck = password.length > 0 ? checkPasswordPolicy(password) : null;
  const passwordProblem = passwordCheck && !passwordCheck.ok ? passwordCheck.reason : null;
  const canSubmit =
    isValidEmail(email) && password.length > 0 && passwordProblem === null && !isLoading;

  async function submit(): Promise<void> {
    setError(null);
    try {
      await createUser({ email: email.trim(), password, role }).unwrap();
      toast.success(`User ${email.trim()} created.`);
      onClose();
    } catch (err) {
      setError(parseApiError(err).message);
    }
  }

  return (
    <Dialog
      title="Create user"
      sub="Create a new account with an email, an initial password you set, and a role."
      onClose={onClose}
    >
      <div className="field">
        <label className="field-label" htmlFor="new-account-address">
          Email
        </label>
        <input
          id="new-account-address"
          className="input"
          autoComplete="off-new-account-address"
          placeholder="person@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="field">
        <label className="field-label" htmlFor="create-password">
          Initial password
        </label>
        <PasswordInput
          id="create-password"
          value={password}
          onChange={setPassword}
          placeholder="Set an initial password"
        />
        {passwordProblem && (
          <div className="field-err" role="alert">
            {passwordProblem}
          </div>
        )}
      </div>
      <RolePicker value={role} onChange={setRole} />
      {error && (
        <div className="field-err" role="alert">
          {error}
        </div>
      )}
      <DialogFoot>
        <button className="btn ghost sm" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary sm" disabled={!canSubmit} onClick={() => void submit()}>
          Create user
        </button>
      </DialogFoot>
    </Dialog>
  );
}

function ChangeUserRoleDialog({
  user,
  onClose
}: {
  user: AdminUserDto;
  onClose: () => void;
}): JSX.Element {
  const [setUserRole, { isLoading }] = useSetUserRoleMutation();
  const toast = useToast();
  const [role, setRole] = useState<UserRole>(user.system_role);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setError(null);
    try {
      await setUserRole({ user_id: user.user_id, role }).unwrap();
      toast.success(`${user.display_name ?? user.email}'s role updated to ${role}.`);
      onClose();
    } catch (err) {
      setError(parseApiError(err).message);
    }
  }

  return (
    <Dialog
      title={`Change ${user.display_name ?? user.email}'s role`}
      sub="The account role is a single source of truth — this also updates their project memberships."
      onClose={onClose}
    >
      <UserPreview user={user} detail={`currently · ${user.system_role}`} />
      <RolePicker value={role} onChange={setRole} />
      {error && (
        <div className="field-err" role="alert">
          {error}
        </div>
      )}
      <DialogFoot>
        <button className="btn ghost sm" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn primary sm"
          disabled={isLoading || role === user.system_role}
          onClick={() => void submit()}
        >
          Change role
        </button>
      </DialogFoot>
    </Dialog>
  );
}

function RolePicker({
  value,
  onChange
}: {
  value: UserRole;
  onChange: (r: UserRole) => void;
}): JSX.Element {
  const roles: UserRole[] = ['admin', 'approver', 'task_submitter'];
  return (
    <div className="field">
      <label className="field-label">Role</label>
      <div className="role-picker">
        {roles.map((r) => (
          <div
            key={r}
            className={`role-pick ${value === r ? 'selected' : ''}`}
            role="radio"
            aria-checked={value === r}
            aria-label={r}
            tabIndex={0}
            onClick={() => onChange(r)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onChange(r);
            }}
          >
            <div className="role-pick-radio" />
            <div>
              <div className="role-pick-name">{r}</div>
              <div className="role-pick-desc">{ROLE_DESCRIPTIONS[r]}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dialog({
  title,
  sub,
  onClose,
  children
}: {
  title: string;
  sub: string;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="overlay open" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-head">
          <h2 className="dialog-title">{title}</h2>
          <p className="dialog-sub">{sub}</p>
        </div>
        <div className="dialog-body">{children}</div>
      </div>
    </div>
  );
}

function DialogFoot({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="dialog-foot">{children}</div>;
}

function UserPreview({ user, detail }: { user: AdminUserDto; detail: string }): JSX.Element {
  return (
    <div
      className="member-cell"
      style={{
        padding: '10px 14px',
        background: 'var(--surface-alt)',
        borderRadius: 'var(--radius-sm)',
        marginBottom: 16
      }}
    >
      <span className="member-avatar alt">{initials(user.display_name ?? user.email)}</span>
      <div>
        <div className="member-name">{user.display_name ?? user.email}</div>
        <div className="member-email">{detail}</div>
      </div>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
}
